import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useMemo } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, TrendingUp, TrendingDown, Loader2, Trash2, CheckCircle2,
  Clock, FileDown, Download, Edit2, Wallet, AlertCircle, History
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const formatNumber = (val) => {
  if (!val && val !== 0) return '';
  const num = typeof val === 'string' ? val.replace(/[.,]/g, '') : String(val);
  const cleaned = num.replace(/[^0-9]/g, '');
  if (!cleaned) return '';
  return Number(cleaned).toLocaleString('tr-TR');
};

const parseNumber = (formatted) => {
  if (!formatted) return 0;
  const cleaned = String(formatted).replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

export default function HesapFinancePage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', type: 'gelir' });

  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [pastPending, setPastPending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [payDialog, setPayDialog] = useState(null); // {entry, amount, method, note}
  const [payHistoryEntry, setPayHistoryEntry] = useState(null);
  const [payHistoryList, setPayHistoryList] = useState([]);
  const todayStr = new Date().toISOString().split('T')[0];
  const monthStartStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;

  const emptyForm = {
    type: 'gelir', amount: '', displayAmount: '', category: '', description: '',
    payment_method: 'nakit', invoice_no: '', kdv_rate: '', kdv_amount: '',
    is_recurring: false, recurring_period: '',
    entry_date: todayStr, status: 'onaylandi'
  };
  const [form, setForm] = useState(emptyForm);
  const [startDate, setStartDate] = useState(monthStartStr);
  const [endDate, setEndDate] = useState(todayStr);

  useEffect(() => { loadEntries(); loadCategories(); }, [filterType, filterCategory, filterStatus, startDate, endDate, pastPending]);

  const loadEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (filterCategory) params.set('category', filterCategory);
      if (filterStatus) params.set('status', filterStatus);
      if (pastPending) params.set('past_pending', 'true');
      if (startDate && !pastPending) params.set('start_date', startDate);
      if (endDate && !pastPending) params.set('end_date', endDate);
      const url = `${API}/finance${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetch(url, { headers });
      if (res.ok) setEntries(await res.json());
    } finally { setLoading(false); }
  };

  const openPayment = (entry) => {
    const remaining = Math.max(0, (entry.amount || 0) - (entry.paid_amount || 0));
    setPayDialog({ entry, amount: remaining, method: entry.payment_method || 'nakit', note: '' });
  };

  const submitPayment = async () => {
    if (!payDialog || payDialog.amount <= 0) { toast.error('Geçerli bir tutar girin'); return; }
    const r = await fetch(`${API}/finance/${payDialog.entry.id}/payment`, {
      method: 'POST', headers,
      body: JSON.stringify({ amount: payDialog.amount, method: payDialog.method, note: payDialog.note })
    });
    if (r.ok) {
      const d = await r.json();
      toast.success(d.entry.status === 'onaylandi' ? 'Tamamı ödendi — onaylandı' : `Kısmi ödeme kaydedildi (${d.entry.paid_amount.toLocaleString('tr-TR')} / ${d.entry.amount.toLocaleString('tr-TR')} TL)`);
      setPayDialog(null);
      loadEntries();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Ödeme başarısız');
    }
  };

  const openPaymentHistory = async (entry) => {
    setPayHistoryEntry(entry);
    const r = await fetch(`${API}/finance/${entry.id}/payments`, { headers });
    if (r.ok) setPayHistoryList(await r.json());
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Bu ödeme silinsin mi?')) return;
    const r = await fetch(`${API}/finance/payments/${paymentId}`, { method: 'DELETE', headers });
    if (r.ok) {
      toast.success('Ödeme silindi');
      openPaymentHistory(payHistoryEntry);
      loadEntries();
    }
  };

  const loadCategories = async () => {
    const res = await fetch(`${API}/categories`, { headers });
    if (res.ok) setCategories(await res.json());
  };

  const openForm = (type) => {
    setEditingEntry(null);
    setForm({ ...emptyForm, type });
    setShowDialog(true);
  };

  const openEditForm = (entry) => {
    setEditingEntry(entry);
    setForm({
      type: entry.type,
      amount: entry.amount,
      displayAmount: formatNumber(entry.amount),
      category: entry.category || '',
      description: entry.description || '',
      payment_method: entry.payment_method || 'nakit',
      invoice_no: entry.invoice_no || '',
      kdv_rate: entry.kdv_rate || '',
      kdv_amount: entry.kdv_amount || '',
      is_recurring: entry.is_recurring || false,
      recurring_period: entry.recurring_period || '',
      entry_date: entry.entry_date || (entry.created_at || '').substring(0, 10),
      status: entry.status || 'onaylandi'
    });
    setShowDialog(true);
  };

  const handleAmountChange = (val) => {
    const display = formatNumber(val);
    const numeric = parseNumber(display);
    const kdvRate = parseFloat(form.kdv_rate) || 0;
    const kdvAmt = numeric * kdvRate / 100;
    setForm({ ...form, amount: numeric, displayAmount: display, kdv_amount: kdvAmt.toFixed(2) });
  };

  const saveEntry = async (e) => {
    e.preventDefault();
    const amount = typeof form.amount === 'number' ? form.amount : parseNumber(form.displayAmount);
    if (!amount) { toast.error('Tutar gerekli'); return; }
    setSaving(true);
    try {
      const body = {
        type: form.type,
        amount,
        category: form.category,
        description: form.description,
        payment_method: form.payment_method,
        invoice_no: form.invoice_no,
        kdv_rate: parseFloat(form.kdv_rate) || 0,
        kdv_amount: parseFloat(form.kdv_amount) || 0,
        entry_date: form.entry_date,
        status: form.status,
        is_recurring: form.is_recurring,
        recurring_period: form.recurring_period
      };

      let url = `${API}/finance`;
      let method = 'POST';
      if (editingEntry) {
        url = `${API}/finance/${editingEntry.id}`;
        method = 'PUT';
      }

      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(editingEntry
        ? 'Kayıt güncellendi'
        : (form.type === 'gelir' ? 'Gelir kaydedildi' : 'Gider kaydedildi'));
      setShowDialog(false);
      setEditingEntry(null);
      loadEntries();
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm('Bu kayıt silinsin mi?')) return;
    await fetch(`${API}/finance/${entry.id}`, { method: 'DELETE', headers });
    toast.success('Kayıt silindi');
    loadEntries();
  };

  const toggleConfirm = async (entry) => {
    try {
      const res = await fetch(`${API}/finance/${entry.id}/confirm`, { method: 'PUT', headers });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.status === 'onaylandi' ? 'Onaylandı' : 'Askıya alındı');
        loadEntries();
      }
    } catch { toast.error('Hata oluştu'); }
  };

  const downloadPDF = async () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const url = `${API}/finance/report-pdf${params.toString() ? '?' + params.toString() : ''}`;
    try {
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'gelir_gider_rapor.pdf';
      link.click();
      toast.success('PDF rapor indirildi');
    } catch { toast.error('PDF oluşturulamadı'); }
  };

  const downloadBackup = async () => {
    try {
      const res = await fetch(`${API}/backup-excel`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'akillicozum_yedek.xlsx';
      link.click();
      toast.success('Excel yedek indirildi');
    } catch { toast.error('Yedek oluşturulamadı'); }
  };

  const addCategory = async () => {
    if (!catForm.name.trim()) return;
    await fetch(`${API}/categories`, { method: 'POST', headers, body: JSON.stringify(catForm) });
    setCatForm({ name: '', type: 'gelir' });
    setShowCatDialog(false);
    loadCategories();
    toast.success('Kategori eklendi');
  };

  const totals = useMemo(() => {
    const onaylanan = entries.filter(e => e.status !== 'askida');
    const askida = entries.filter(e => e.status === 'askida');
    return {
      gelir: onaylanan.filter(e => e.type === 'gelir').reduce((s, e) => s + e.amount, 0),
      gider: onaylanan.filter(e => e.type === 'gider').reduce((s, e) => s + e.amount, 0),
      askidaGelir: askida.filter(e => e.type === 'gelir').reduce((s, e) => s + e.amount, 0),
      askidaGider: askida.filter(e => e.type === 'gider').reduce((s, e) => s + e.amount, 0),
    };
  }, [entries]);

  const filteredCats = categories.filter(c => c.type === form.type || c.type === 'genel');
  const uniqueCats = [...new Set(entries.map(e => e.category).filter(Boolean))];

  // Client-side arama filtresi (açıklama, kategori, fatura no, tutar)
  const visibleEntries = entries.filter(e => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (e.description || '').toLowerCase().includes(q)
      || (e.category || '').toLowerCase().includes(q)
      || (e.invoice_no || '').toLowerCase().includes(q)
      || String(e.amount || '').includes(q);
  });
  const payMethodLabel = { nakit: 'Nakit', kart: 'Kart', havale: 'Havale', cek: 'Çek' };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="hesap-finance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Gelir / Gider</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowCatDialog(true)} data-testid="category-btn">Kategori</Button>
          <Button variant="outline" size="sm" onClick={downloadPDF} data-testid="pdf-report-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" />PDF Rapor
          </Button>
          <Button variant="outline" size="sm" onClick={downloadBackup} data-testid="backup-btn">
            <Download className="h-3.5 w-3.5 mr-1" />Excel Yedek
          </Button>
          <Button onClick={() => openForm('gelir')} className="bg-green-600 hover:bg-green-700" data-testid="add-gelir-btn">
            <TrendingUp className="h-4 w-4 mr-1" />Gelir Ekle
          </Button>
          <Button onClick={() => openForm('gider')} className="bg-red-600 hover:bg-red-700" data-testid="add-gider-btn">
            <TrendingDown className="h-4 w-4 mr-1" />Gider Ekle
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500">{`Gelir (Onaylı)`}</p>
          <p className="text-lg font-bold text-green-600">{totals.gelir.toLocaleString('tr-TR')} TL</p>
          {totals.askidaGelir > 0 && <p className="text-xs text-amber-500">{`Askıda: ${totals.askidaGelir.toLocaleString('tr-TR')} TL`}</p>}
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500">{`Gider (Onaylı)`}</p>
          <p className="text-lg font-bold text-red-600">{totals.gider.toLocaleString('tr-TR')} TL</p>
          {totals.askidaGider > 0 && <p className="text-xs text-amber-500">{`Askıda: ${totals.askidaGider.toLocaleString('tr-TR')} TL`}</p>}
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500">Net Kar/Zarar</p>
          <p className={`text-lg font-bold ${totals.gelir - totals.gider >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {(totals.gelir - totals.gider).toLocaleString('tr-TR')} TL
          </p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500">{`Kayıt Sayısı`}</p>
          <p className="text-lg font-bold text-slate-700">{entries.length}</p>
          <p className="text-xs text-slate-400">
            {(startDate || endDate) ? `${startDate || '...'} - ${endDate || '...'}` : `Tüm kayıtlar`}
          </p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{`Başlangıç`}</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="h-8 w-36 text-sm" data-testid="filter-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{`Bitiş`}</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="h-8 w-36 text-sm" data-testid="filter-end-date" />
            </div>
            <div className="flex gap-1">
              {[['', `Tümü`], ['gelir', 'Gelir'], ['gider', 'Gider']].map(([v, l]) => (
                <button key={v} onClick={() => setFilterType(v)}
                  className={`px-3 py-1.5 rounded text-xs ${filterType === v ? 'bg-slate-800 text-white' : 'bg-slate-100 hover:bg-slate-200'}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="flex gap-1 flex-wrap">
              {[['', 'Hepsi'], ['onaylandi', `Onaylı`], ['askida', `Askıda`]].map(([v, l]) => (
                <button key={v} onClick={() => { setFilterStatus(v); setPastPending(false); }}
                  className={`px-3 py-1.5 rounded text-xs ${!pastPending && filterStatus === v ? 'bg-amber-600 text-white' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`}>
                  {l}
                </button>
              ))}
              <button onClick={() => { setPastPending(true); setFilterStatus(''); }}
                className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${pastPending ? 'bg-red-600 text-white' : 'bg-red-50 hover:bg-red-100 text-red-700'}`}
                data-testid="filter-past-pending">
                <AlertCircle className="h-3 w-3" />Geçmiş Askıdakiler
              </button>
            </div>
            {uniqueCats.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Kategori</Label>
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="filter-category">
                  <option value="">{`Tümü`}</option>
                  {uniqueCats.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {(startDate || endDate || filterType || filterCategory || filterStatus || searchQuery) && (
              <Button variant="ghost" size="sm" className="text-xs h-8"
                onClick={() => { setStartDate(''); setEndDate(''); setFilterType(''); setFilterCategory(''); setFilterStatus(''); setSearchQuery(''); }}>
                Temizle
              </Button>
            )}
          </div>
          {/* Arama satırı */}
          <div className="mt-2">
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Açıklama, kategori, fatura no veya tutar ile ara..."
              className="h-8 text-sm" data-testid="finance-search-input" />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {visibleEntries.map(entry => {
          const isAskida = entry.status === 'askida';
          const paidAmount = Number(entry.paid_amount || 0);
          const totalAmount = Number(entry.amount || 0);
          const remaining = Math.max(0, totalAmount - paidAmount);
          const hasPartial = paidAmount > 0 && paidAmount < totalAmount;
          const paidPct = totalAmount > 0 ? Math.min(100, (paidAmount / totalAmount) * 100) : 0;
          return (
            <div key={entry.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg border gap-2 ${isAskida ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}
              data-testid={`finance-entry-${entry.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                {entry.type === 'gelir'
                  ? <TrendingUp className={`h-4 w-4 flex-shrink-0 ${isAskida ? 'text-amber-500' : 'text-green-500'}`} />
                  : <TrendingDown className={`h-4 w-4 flex-shrink-0 ${isAskida ? 'text-amber-500' : 'text-red-500'}`} />
                }
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium truncate">
                      {entry.description || (entry.type === 'gelir' ? 'Gelir' : 'Gider')}
                    </p>
                    {isAskida && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-200 text-amber-800 flex-shrink-0">
                        <Clock className="h-3 w-3" />{`Askıda`}
                      </span>
                    )}
                    {hasPartial && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700 flex-shrink-0" data-testid="partial-badge">
                        <Wallet className="h-3 w-3" />Kısmi ödendi
                      </span>
                    )}
                    {!isAskida && entry.confirmed_at && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700 flex-shrink-0">
                        <CheckCircle2 className="h-3 w-3" />{`Onaylı`}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-2 text-xs text-slate-400">
                    <span>{entry.entry_date || (entry.created_at || '').substring(0, 10)}</span>
                    {entry.category && <span>{entry.category}</span>}
                    <span>{payMethodLabel[entry.payment_method] || entry.payment_method}</span>
                    {entry.invoice_no && <span>Fatura: {entry.invoice_no}</span>}
                    {entry.kdv_amount > 0 && <span>KDV: {entry.kdv_amount} TL</span>}
                    {entry.confirmed_at && !isAskida && (
                      <span className="text-green-600">Onay: {entry.confirmed_at.substring(0, 10)}</span>
                    )}
                  </div>
                  {(isAskida || hasPartial) && paidAmount > 0 && (
                    <div className="mt-1.5">
                      <div className="flex items-center justify-between text-[10px] text-slate-600">
                        <span>Ödenen: <b className="text-emerald-700">{paidAmount.toLocaleString('tr-TR')} TL</b></span>
                        <span>Kalan: <b className="text-amber-700">{remaining.toLocaleString('tr-TR')} TL</b></span>
                      </div>
                      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mt-0.5">
                        <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${paidPct}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                <div className="text-right mr-1">
                  <p className={`font-bold ${isAskida ? 'text-amber-600' : entry.type === 'gelir' ? 'text-green-600' : 'text-red-600'}`}>
                    {entry.type === 'gelir' ? '+' : '-'}{totalAmount.toLocaleString('tr-TR')} TL
                  </p>
                </div>
                {isAskida && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-800" onClick={() => openPayment(entry)}
                    title="Kısmi/Tam Ödeme Yap" data-testid={`pay-btn-${entry.id}`}>
                    <Wallet className="h-4 w-4" />
                  </Button>
                )}
                {paidAmount > 0 && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-slate-700" onClick={() => openPaymentHistory(entry)}
                    title="Ödeme Geçmişi" data-testid={`history-btn-${entry.id}`}>
                    <History className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500 hover:text-blue-700"
                  onClick={() => openEditForm(entry)} title={`Düzenle`}>
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className={`h-7 w-7 ${isAskida ? 'text-green-600 hover:text-green-800' : 'text-amber-500 hover:text-amber-700'}`}
                  onClick={() => toggleConfirm(entry)} data-testid={`confirm-btn-${entry.id}`}
                  title={isAskida ? 'Onayla' : `Askıya al`}>
                  {isAskida ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => deleteEntry(entry)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
        {entries.length === 0 && <p className="text-center text-slate-400 py-8">{pastPending ? 'Geçmişte askıda kalmış kayıt yok — her şey ödendi 🎉' : `Henüz kayıt yok`}</p>}
      </div>

      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setEditingEntry(null); } setShowDialog(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingEntry ? `Kayıt Düzenle` : (form.type === 'gelir' ? 'Gelir Ekle' : 'Gider Ekle')}</DialogTitle></DialogHeader>
          <form onSubmit={saveEntry} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label>Tutar (TL) *</Label>
                <Input value={form.displayAmount || formatNumber(form.amount)}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="0" data-testid="entry-amount" />
              </div>
              <div className="space-y-1">
                <Label>Tarih</Label>
                <Input type="date" value={form.entry_date}
                  onChange={(e) => setForm({...form, entry_date: e.target.value})} data-testid="entry-date" />
              </div>
              <div className="space-y-1">
                <Label>Durum</Label>
                <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="entry-status">
                  <option value="onaylandi">{`Onaylandı (Kesinleşmiş)`}</option>
                  <option value="askida">{`Askıda (Henüz yatmadı)`}</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Kategori</Label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="entry-category">
                  <option value="">{`Seçiniz`}</option>
                  {filteredCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>{`Ödeme Yöntemi`}</Label>
                <select value={form.payment_method} onChange={(e) => setForm({...form, payment_method: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="nakit">Nakit</option>
                  <option value="kart">Kart</option>
                  <option value="havale">Havale/EFT</option>
                  <option value="cek">{`Çek`}</option>
                </select>
              </div>
              <div className="col-span-2 space-y-1">
                <Label>{`Açıklama`}</Label>
                <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder={`Açıklama`} />
              </div>
              <div className="space-y-1">
                <Label>Fatura No</Label>
                <Input value={form.invoice_no} onChange={(e) => setForm({...form, invoice_no: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>KDV (%)</Label>
                <Input type="number" value={form.kdv_rate} onChange={(e) => {
                  const rate = parseFloat(e.target.value) || 0;
                  const amount = typeof form.amount === 'number' ? form.amount : parseNumber(form.displayAmount);
                  const amt = amount * rate / 100;
                  setForm({...form, kdv_rate: e.target.value, kdv_amount: amt.toFixed(2)});
                }} />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowDialog(false); setEditingEntry(null); }} className="flex-1">{`İptal`}</Button>
              <Button type="submit" disabled={saving} className={`flex-1 ${form.type === 'gelir' ? 'bg-green-600' : 'bg-red-600'}`}
                data-testid="save-entry-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingEntry ? `Güncelle` : 'Kaydet')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Kısmi Ödeme Dialog */}
      <Dialog open={!!payDialog} onOpenChange={(o) => { if (!o) setPayDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" />Ödeme Al</DialogTitle></DialogHeader>
          {payDialog && (() => {
            const e = payDialog.entry;
            const remaining = Math.max(0, (e.amount || 0) - (e.paid_amount || 0));
            const paidPct = e.amount > 0 ? Math.min(100, ((e.paid_amount || 0) / e.amount) * 100) : 0;
            return (
              <div className="space-y-3">
                <div className="bg-slate-50 rounded p-2 text-xs space-y-1">
                  <div className="flex justify-between"><span>Kayıt:</span><b>{e.description}</b></div>
                  <div className="flex justify-between"><span>Toplam:</span><b>{e.amount.toLocaleString('tr-TR')} TL</b></div>
                  <div className="flex justify-between"><span>Ödenen:</span><b className="text-emerald-700">{(e.paid_amount || 0).toLocaleString('tr-TR')} TL</b></div>
                  <div className="flex justify-between"><span>Kalan (askıda):</span><b className="text-amber-700">{remaining.toLocaleString('tr-TR')} TL</b></div>
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${paidPct}%` }} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Alınan Tutar (TL)</Label>
                  <Input type="number" step="0.01" max={remaining} value={payDialog.amount}
                    onChange={ev => setPayDialog({ ...payDialog, amount: ev.target.value })} data-testid="pay-amount" />
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => setPayDialog({ ...payDialog, amount: remaining })} className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Tamamını Al</button>
                    <button onClick={() => setPayDialog({ ...payDialog, amount: remaining / 2 })} className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Yarısını Al</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Ödeme Yöntemi</Label>
                    <select value={payDialog.method} onChange={ev => setPayDialog({ ...payDialog, method: ev.target.value })}
                      className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
                      <option value="nakit">Nakit</option>
                      <option value="kart">Kart</option>
                      <option value="havale">Havale/EFT</option>
                      <option value="cek">Çek/Senet</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Not (opsiyonel)</Label>
                    <Input value={payDialog.note} onChange={ev => setPayDialog({ ...payDialog, note: ev.target.value })} placeholder="Örn: 1. taksit" />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={() => setPayDialog(null)} className="flex-1">İptal</Button>
                  <Button onClick={submitPayment} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="pay-submit">
                    <Wallet className="h-4 w-4 mr-1" />Ödeme Kaydet
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Ödeme Geçmişi Dialog */}
      <Dialog open={!!payHistoryEntry} onOpenChange={(o) => { if (!o) { setPayHistoryEntry(null); setPayHistoryList([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" />Ödeme Geçmişi</DialogTitle></DialogHeader>
          {payHistoryEntry && (
            <div className="space-y-2">
              <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">
                <b>{payHistoryEntry.description}</b> · Toplam {payHistoryEntry.amount.toLocaleString('tr-TR')} TL · Ödenen {(payHistoryEntry.paid_amount || 0).toLocaleString('tr-TR')} TL
              </div>
              {payHistoryList.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-4">Ödeme kaydı yok</p>
              ) : (
                <div className="space-y-1">
                  {payHistoryList.map(p => (
                    <div key={p.id} className="flex items-center justify-between border rounded p-2 text-xs">
                      <div>
                        <b className="text-emerald-700">{p.amount.toLocaleString('tr-TR')} TL</b>
                        <span className="text-slate-500 ml-2">· {payMethodLabel[p.method] || p.method}</span>
                        {p.note && <p className="text-[10px] text-slate-400 mt-0.5">{p.note}</p>}
                        <p className="text-[10px] text-slate-400">{new Date(p.at).toLocaleString('tr-TR')} · {p.by}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400" onClick={() => deletePayment(p.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => { setPayHistoryEntry(null); setPayHistoryList([]); }}>Kapat</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Yeni Kategori</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={catForm.name} onChange={(e) => setCatForm({...catForm, name: e.target.value})} placeholder={`Kategori adı`} />
            <select value={catForm.type} onChange={(e) => setCatForm({...catForm, type: e.target.value})}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
              <option value="gelir">Gelir Kategorisi</option>
              <option value="gider">Gider Kategorisi</option>
              <option value="genel">Genel</option>
            </select>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCatDialog(false)} className="flex-1">{`İptal`}</Button>
              <Button onClick={addCategory} className="flex-1">Ekle</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
